import { fixedHeaderEnabled } from 'lib/fixedHeader'
import React, {
    useCallback,
    useDeferredValue,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { isMobile } from 'lib/device'
import cn from 'classnames'
import useStore from 'state/knovStore'
import { consumeSpaceNotificationBadge } from 'refactor/hooks/api/notificationBadgeRefetchPolicy'
import useGetQuests, { serializeFilterKey } from 'refactor/hooks/api/useGetQuests'
import useGetQuest from 'refactor/hooks/api/useGetQuest'
import usePanelContext from 'refactor/hooks/usePanelContext'
import ErrorBoundary from 'components/shared/ErrorBoundary'
import SearchHeader from 'components/panels/SearchHeader'
import Search from 'components/panels/Search'
import UserProfile from 'components/panels/UserProfile'
import DirectMessageStarter from 'components/panels/DirectMessageStarter'
import OrdinalsMediaStream from 'components/panels/OrdinalsMediaStream'
import NaturalFeedSegments from 'components/panels/NaturalFeedSegments'
import type { NaturalFeedIdleTaskQueue } from 'lib/naturalFeedIdleTasks'
import PanelLoadingIndicator from 'components/panels/PanelLoadingIndicator'
import VirtualQuestViewport from 'components/panels/VirtualQuestViewport'
import QuestOrig from 'components/quests/Quest'
import VirtualQuestInView from 'components/quests/VirtualQuestInView'
import QuestNotFound from 'components/quests/QuestNotFound'
import ThinQuestOrig from 'components/quests/ThinQuest'
import questModel from 'components/quests/questModel'
import { cacheQuest, getCachedQuest, getCachedStreamCardQuest } from 'state/cache'
const Quest = QuestOrig as any
const ThinQuest = ThinQuestOrig as any
import styles from 'components/panels/panel.module.scss'
import { logEv } from 'lib/log'
import { withDepth, Filter } from 'components/filters/useStreamFilters'
import { setFirstTouchAuthSurface, QUALIFYING_SURFACES } from 'lib/authSurfaceTracking'
import { ScrollResizeCompensationProvider } from 'lib/scrollResizeCompensationContext'
import { RowGeometryEventsProvider } from 'lib/rowGeometryEvents'
import { readStaticQuestRowMeasurement } from 'lib/staticGeometryRowMeasurements'
import { useStaticGeometryRowMeasurements } from 'lib/useStaticGeometryRowMeasurements'
import { mediaBucketsEnabled, naturalFeedEnabled } from 'lib/mediaBuckets'
import { buildNaturalFeedSegments } from 'lib/naturalFeedSegments'
import type { NaturalFeedSegment as NaturalFeedSegmentModel } from 'lib/naturalFeedSegments'
import {
    estimateQuestStreamHeight,
    shouldRemeasureRowWidth,
    QUEST_STREAM_DEFAULT_WIDTH_ESTIMATE_PX,
} from 'lib/questStreamHeight'
import { resolveInitialQuestGeometry, resolveQuestGeometryRevision } from 'lib/questGeometryFrame'
import { applyStreamQuestRowContext, streamQuestRowKey } from 'refactor/hooks/api/streamQuestRows'
import type { NaturalFeedQuestDescriptor } from 'lib/naturalFeedSession'
import type { Quest as QuestType } from 'types/quests'
import { Lock } from 'lucide-react'
import useTeamLookup from 'components/teams/useTeamLookup'
import {
    doesStreamQuestNeedHydration,
    isReadyFeedCardProjection,
} from 'refactor/hooks/api/streamQuestHydration'
import { queueStreamQuestHydration } from 'state/streamQuestHydrationQueue'
import { refetchStreamAfterExplicitInvalidation } from 'api/queryPolicies'
import { feedPathFromFilter, initialFeedPageFromUrl, feedPageHref } from 'lib/feedPagination'
import FeedNextPageLink from 'components/shared/FeedNextPageLink'
import { isRadioStreamFilter } from './feedNewItemsEligibility'

interface QuestStreamProps {
    filter: Filter
    hideSearchHeader?: boolean
}

type QuestStreamVirtualRow = (
    | { type: 'inline-draft'; quest: QuestType; ix: number }
    | { type: 'inline-rejected'; quest: any; ix: number }
    | { type: 'inline-posted'; quest: QuestType; ix: number }
    | { type: 'pinned'; quest: QuestType; ix: number }
    | { type: 'quest'; quest: QuestType; ix: number; isLastQuest: boolean }
) & { naturalFeedDescriptor?: NaturalFeedQuestDescriptor }

type QuestStreamSourceRow = {
    quest: QuestType
    ix: number
    naturalFeedDescriptor?: NaturalFeedQuestDescriptor
}

type QuestStreamGeometryState = {
    revision: number
    expandedAnswerIds: string[]
    expandedTweetAnswerIds: string[]
    // Deliberately OUTSIDE the revision: v1 keys rotate on revision bumps, and a remount
    // wipes the composer's local state (the editor would close itself). The v2 signature
    // appends this separately; v1 rows only get the clip lift.
    editingAnswerIds: string[]
}

function joinGeometryIds(label: string, ids: string[] | undefined) {
    return ids?.length ? `${label}:${ids.join(',')}` : null
}

function resolveQuestStreamResizeIntentSignature(state?: QuestStreamGeometryState) {
    if (!state) return null

    return (
        [
            joinGeometryIds('answers', state.expandedAnswerIds),
            joinGeometryIds('tweets', state.expandedTweetAnswerIds),
            joinGeometryIds('editing', state.editingAnswerIds),
        ]
            .filter(Boolean)
            .join('|') || null
    )
}

const STREAM_VIRTUAL_OVERSCAN = 6
const STREAM_PAGE_PREFETCH_DISTANCE_PX = 3200
const STREAM_PAGINATION_TAIL_RESERVE_PX = 9600
const STREAM_PAGINATION_TAIL_MIN_PX = 1200
// Tighter card rhythm on phones: full-bleed cards read as a continuous column,
// so the inter-card gap shrinks with them. This constant is both virtualizer
// geometry (row offsets) and the natural-row margin, so it stays consistent.
const STREAM_QUEST_ROW_GAP_PX = isMobile ? 18 : 30
const NATURAL_FEED_PREFETCH_ROOT_MARGIN = '22000px 0px 22000px 0px'
const NATURAL_FEED_FORWARD_PREFETCH_ROOT_MARGIN = '3000px 0px 22000px 0px'
const NATURAL_FEED_BACKWARD_PREFETCH_ROOT_MARGIN = '22000px 0px 3000px 0px'

type IdleWindow = Window &
    typeof globalThis & {
        requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number
        cancelIdleCallback?: (handle: number) => void
    }

function rowUsesCardGeometry(row: QuestStreamVirtualRow | null | undefined) {
    return row?.type !== 'inline-draft' && row?.type !== 'inline-rejected'
}

function Loading() {
    return <PanelLoadingIndicator label="Loading stream" />
}

function PrivateStreamPlaceholder() {
    return (
        <div className={styles.privateStreamPlaceholder}>
            <div className={styles.privateStreamLock}>
                <Lock size={38} strokeWidth={2.4} />
            </div>
            <div className={styles.privateStreamMessage}>This channel is private</div>
        </div>
    )
}

function NaturalFeedHydratedQuestRow({
    descriptor,
    estimatedHeight,
    isPanelHidden,
    isPanelPrewarming,
    taskQueue,
    render,
}: {
    descriptor: NaturalFeedQuestDescriptor
    estimatedHeight: number
    isPanelHidden: boolean
    isPanelPrewarming: boolean
    taskQueue: NaturalFeedIdleTaskQueue
    render: (quest: QuestType) => React.ReactNode
}) {
    const questId = descriptor.row?.id == null ? undefined : String(descriptor.row.id)
    const {
        quest: observedQuest,
        dataUpdatedAt,
        error,
    } = useGetQuest(questId, {
        ensureStreamHydration: true,
        skipStreamQueryTouch: true,
    })
    const cachedStreamCardQuest = useMemo(
        () =>
            getCachedStreamCardQuest(
                questId,
                descriptor.row.feed_card?.preview_answer_ids?.length
                    ? descriptor.row.feed_card.preview_answer_ids
                    : descriptor.row.matching_answer_ids || [],
            ),
        [dataUpdatedAt, descriptor.row, observedQuest, questId],
    )
    const quest = cachedStreamCardQuest || observedQuest
    const contextualQuest = useMemo(
        () => applyStreamQuestRowContext(quest, descriptor.row),
        [descriptor.row, quest],
    )
    const readyQuestId =
        contextualQuest && !doesStreamQuestNeedHydration(contextualQuest)
            ? String(contextualQuest.id)
            : null
    const readyFeedCardProjection = isReadyFeedCardProjection(contextualQuest)

    useEffect(() => {
        if (isPanelHidden || !questId || readyQuestId) return
        queueStreamQuestHydration([questId], { delayMs: 0 })
    }, [isPanelHidden, questId, readyQuestId])

    const [promotedQuest, setPromotedQuest] = useState<QuestType | null>(() =>
        readyQuestId && contextualQuest && (readyFeedCardProjection || isPanelPrewarming)
            ? contextualQuest
            : null,
    )

    useEffect(() => {
        if (!readyQuestId || !contextualQuest) return
        if (promotedQuest === contextualQuest) return
        if (readyFeedCardProjection || isPanelPrewarming) {
            setPromotedQuest(contextualQuest)
            return
        }

        return taskQueue.enqueue(`quest-row:${descriptor.rowKey}:activate`, 'activate', () => {
            setPromotedQuest(contextualQuest)
        })
    }, [
        contextualQuest,
        descriptor.rowKey,
        isPanelPrewarming,
        promotedQuest,
        readyFeedCardProjection,
        readyQuestId,
        taskQueue,
    ])

    if (error) {
        return (
            <div className={cn(styles.questContainer, 'quest-container')}>
                <QuestNotFound error={error} />
            </div>
        )
    }

    if (!promotedQuest || String(promotedQuest.id) !== questId) {
        return (
            <div
                aria-hidden="true"
                data-natural-flow-geometry-pending="true"
                data-natural-feed-geometry-pending="true"
                className={styles.questInViewPlaceholder}
                style={{ minHeight: Math.max(240, estimatedHeight) }}
            />
        )
    }

    return <>{render(promotedQuest)}</>
}

function QuestStreamQuests({ filter, hideSearchHeader = false }: QuestStreamProps) {
    const { panel, hide, panelContentPrewarming, scrollContainerRef, setIsRefreshing } =
        usePanelContext()
    const staticGeometryEnabled = mediaBucketsEnabled()
    const naturalFlowEnabled = naturalFeedEnabled()
    const panelId = panel.panelId
    const params = filter?.notifications ? { clear: true } : {}
    const hasSingleTeamFilter = filter?.team_ids?.length === 1
    // fixed_header: a channel's root-thread cards drop their crumb strip.
    const omitRootCrumb = fixedHeaderEnabled() && hasSingleTeamFilter
    const streamTeamId = hasSingleTeamFilter ? filter.team_ids[0] : null
    const {
        team: streamTeam,
        isLoading: isTeamLoading,
        isPrivateStub: isPrivateStreamStub,
    } = useTeamLookup(streamTeamId)
    const [isRefreshing, setLocalIsRefreshing] = useState(false)
    // Use the shared refresh state if available, otherwise fallback to local state
    const handleSetIsRefreshing = value => {
        if (setIsRefreshing) {
            setIsRefreshing(value)
        } else {
            setLocalIsRefreshing(value)
        }
    }

    useEffect(function onMount() {
        if (!window.gon?.currentUser) {
            setFirstTouchAuthSurface(QUALIFYING_SURFACES.STREAM)
        }

        // Clear the badge when the notification panel loads. The per-space count
        // goes too, or a remount (e.g. Back to home) restores the old number.
        if (filter?.notifications) {
            const { activeSpaceId } = useStore.getState()
            useStore.getState().set(state => consumeSpaceNotificationBadge(state, activeSpaceId))
        }
        logEv('STREAM VIEW', { filter })
    }, [])

    const shouldFetchQuests =
        !isPrivateStreamStub && !(hasSingleTeamFilter && isTeamLoading && !!streamTeamId)
    // Linked pagination for URL-addressable feeds (/meme/:text, /stream/:id):
    // a hard load at ?page=N starts the feed at that batch, and the feed tail
    // renders a real next-page <a href> — crawlers walk the chain page by
    // page, humans keep infinite scroll. Panels without their own URL opt out.
    const seoFeedPath = feedPathFromFilter(filter)
    const seoInitialPage = initialFeedPageFromUrl(seoFeedPath)

    const {
        quests,
        pinnedQuest,
        fetchNextPage,
        isFetching,
        isFetchingNextPage,
        isError,
        refetch,
        hasNextPage,
        nextPage,
        naturalFeedRows,
        naturalFeedPinnedQuest,
        naturalFeedSessionKey,
    } = useGetQuests(withDepth(filter), {
        enabled: shouldFetchQuests,
        radioListening: !hide && !panelContentPrewarming,
        params,
        initialPage: seoInitialPage,
        // Natural flow keeps row identity and settled geometry in a lightweight session.
        // React Query is bounded transport; mounted rows own hydrated entities.
        retainFeedSession: naturalFlowEnabled,
        unboundedPages: !naturalFlowEnabled,
        // Swiper virtualizes offscreen panels, so a swipe can remount this observer.
        // Only an explicit navigation/reload invalidation should refresh cached rows.
        refetchOnMount: refetchStreamAfterExplicitInvalidation,
    })

    // Show PTR spinner for background refetch when we already have cached items
    useEffect(() => {
        // Only show top spinner for standard refetches, not infinite-scroll pagination
        if (!setIsRefreshing) return
        const loadedRows = naturalFlowEnabled ? naturalFeedRows.length : quests?.length || 0
        if (isFetching && !isFetchingNextPage && loadedRows > 0) {
            setIsRefreshing(true)
        } else {
            setIsRefreshing(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetching, isFetchingNextPage, naturalFeedRows.length, naturalFlowEnabled, quests?.length])

    const isStarred = filter.starred
    const isThinQuest = isStarred
    const isUserProfile = filter.user || filter?.user_ordinals
    const isSearch = filter.search
    const isNotifications = !!filter?.notifications
    const isClips = !!filter?.clip || !!filter?.public_clip
    // Direct Messages leads with its starter (DirectMessageStarter) instead.
    const isDirectMessages = !!filter?.private_messages
    // Only the station opens threads in the Music stream, so it has no composer.
    const isRadioStream = isRadioStreamFilter(filter)
    const showInlineNewQuest =
        !isRadioStream &&
        !isSearch &&
        !isUserProfile &&
        !isThinQuest &&
        !isNotifications &&
        !isClips &&
        !isDirectMessages &&
        // Music is for songs and the station; threads start from the composer elsewhere.
        !isRadioStreamFilter(filter)
    const inlineDraftFilterKey = serializeFilterKey(withDepth(filter))
    const [inlinePostedQuests, setInlinePostedQuests] = useState([])
    const [inlineDraftQuest, setInlineDraftQuest] = useState<QuestType | null>(null)
    const questStreamRootRef = useRef<HTMLDivElement | null>(null)
    const questListElementRef = useRef<HTMLDivElement | null>(null)
    const streamPageFetchInFlightRef = useRef(false)
    const naturalPaginationSentinelRef = useRef<HTMLDivElement | null>(null)
    const previousLoadedStreamHeightRef = useRef<number | null>(null)
    const streamScrollDirectionRef = useRef<'down' | 'up' | 'none'>('none')
    const [questStreamWidth, setQuestStreamWidth] = useState(QUEST_STREAM_DEFAULT_WIDTH_ESTIMATE_PX)
    const [paginationTailHeight, setPaginationTailHeight] = useState(
        STREAM_PAGINATION_TAIL_RESERVE_PX,
    )
    const paginationTailCarryRef = useRef(0)
    const [questGeometryStateById, setQuestGeometryStateById] = useState<
        Record<string, QuestStreamGeometryState>
    >({})

    // Single reducer for every per-quest geometry-affecting UI set (answer-text expansion,
    // tweet expansion, ...): toggle membership, bump the revision only on a real change, and
    // preserve every other field so the channels can't wipe each other's state.
    const toggleQuestGeometryMembership = useCallback(
        (
            field: 'expandedAnswerIds' | 'expandedTweetAnswerIds' | 'editingAnswerIds',
            questId: string | number | null | undefined,
            answerId: string | number | null | undefined,
            isMember: boolean,
        ) => {
            if (!questId || !answerId) return

            const normalizedQuestId = String(questId)
            const normalizedAnswerId = String(answerId)
            setQuestGeometryStateById(current => {
                const currentState = current[normalizedQuestId] || {
                    revision: 0,
                    expandedAnswerIds: [],
                    expandedTweetAnswerIds: [],
                    editingAnswerIds: [],
                }
                const members = new Set(currentState[field] || [])
                if (members.has(normalizedAnswerId) === isMember) return current
                if (isMember) members.add(normalizedAnswerId)
                else members.delete(normalizedAnswerId)

                return {
                    ...current,
                    [normalizedQuestId]: {
                        ...currentState,
                        // Editing stays out of the revision so v1 keys never rotate mid-edit
                        // (a remount would close the composer); every other geometry set
                        // invalidates both key (v1) and signature (v2) through the bump.
                        revision:
                            field === 'editingAnswerIds'
                                ? currentState.revision
                                : currentState.revision + 1,
                        [field]: Array.from(members).sort(),
                    },
                }
            })
        },
        [],
    )

    const handleAnswerEditingChange = useCallback(
        ({
            questId,
            answerId,
            editing,
        }: {
            questId?: string | number | null
            answerId?: string | number | null
            editing: boolean
        }) => toggleQuestGeometryMembership('editingAnswerIds', questId, answerId, editing),
        [toggleQuestGeometryMembership],
    )

    const handleAnswerTextCollapsedChange = useCallback(
        ({
            questId,
            answerId,
            textIsCollapsed,
        }: {
            questId?: string | number | null
            answerId?: string | number | null
            textIsCollapsed: boolean
        }) =>
            toggleQuestGeometryMembership('expandedAnswerIds', questId, answerId, !textIsCollapsed),
        [toggleQuestGeometryMembership],
    )

    // Tweet cards grow in place on their own "more" toggle; report it as a geometry event so
    // the row's reserve follows the rendered height (via the v2 resize channel) instead of the
    // expanded text being clipped by the pinned row frame.
    const handleTweetExpandedChange = useCallback(
        ({
            questId,
            answerId,
            expanded,
        }: {
            questId?: string | number | null
            answerId?: string | number | null
            expanded: boolean
        }) => toggleQuestGeometryMembership('expandedTweetAnswerIds', questId, answerId, expanded),
        [toggleQuestGeometryMembership],
    )

    const createInlineDraftQuest = () => {
        const draftQuest = questModel.newQuestFromFilter({}, filter, { team: streamTeam })
        cacheQuest(draftQuest)
        return draftQuest
    }

    const highlightInlinePostedQuest = (quest, explicitAnswerId?) => {
        const panelApi = panel
        const answerId = explicitAnswerId || quest?.parent_id || quest?.parent?.id
        if (!answerId) return

        void panelApi.requestAnswerReveal?.(answerId, {
            shakeQuestId: quest?.id,
            highlight: true,
        })
    }

    useEffect(() => {
        if (!showInlineNewQuest) {
            setInlineDraftQuest(null)
            return
        }

        setInlineDraftQuest(createInlineDraftQuest())
        setInlinePostedQuests([])
    }, [showInlineNewQuest, inlineDraftFilterKey, streamTeam])

    const handleInlineQuestPublished = (publishedQuest, highlightAnswerId?) => {
        if (!publishedQuest?.id) return

        setInlinePostedQuests(prev => {
            const deduped = prev.filter(q => q?.id && q.id !== publishedQuest.id)
            return [publishedQuest, ...deduped]
        })

        setInlineDraftQuest(createInlineDraftQuest())

        // Mirror panel-insert behavior: nudge panel and highlight the newly posted thread's title answer.
        highlightInlinePostedQuest(publishedQuest, highlightAnswerId)
    }

    const pinnedQuestRef =
        naturalFlowEnabled && naturalFeedPinnedQuest
            ? (naturalFeedPinnedQuest.row as QuestType)
            : pinnedQuest
    const pinnedQuestId = pinnedQuestRef?.id
    const sourceStreamQuestRows = useMemo<QuestStreamSourceRow[]>(
        () =>
            naturalFlowEnabled
                ? naturalFeedRows.map((descriptor, ix) => ({
                      quest: descriptor.row as QuestType,
                      ix,
                      naturalFeedDescriptor: descriptor,
                  }))
                : (quests || []).map((quest, ix) => ({ quest, ix })),
        [naturalFeedRows, naturalFlowEnabled, quests],
    )
    const streamQuestIds = useMemo(
        () => new Set(sourceStreamQuestRows.map(row => row.quest?.id).filter(Boolean)),
        [sourceStreamQuestRows],
    )
    const visibleInlinePostedQuests = useMemo(
        () =>
            inlinePostedQuests.filter(
                quest => quest?.id && quest.id !== pinnedQuestId && !streamQuestIds.has(quest.id),
            ),
        [inlinePostedQuests, pinnedQuestId, streamQuestIds],
    )
    const visibleInlineRejectedQuests = useMemo(
        () => visibleInlinePostedQuests.filter(quest => quest?.localPublishErrorStatus),
        [visibleInlinePostedQuests],
    )
    const visibleInlineValidPostedQuests = useMemo(
        () => visibleInlinePostedQuests.filter(quest => !quest?.localPublishErrorStatus),
        [visibleInlinePostedQuests],
    )
    const streamQuestRows = useMemo(
        () => sourceStreamQuestRows.filter(row => row.quest?.id),
        [sourceStreamQuestRows],
    )
    const virtualContentRows = useMemo<QuestStreamVirtualRow[]>(() => {
        const rows: QuestStreamVirtualRow[] = []

        if (showInlineNewQuest && inlineDraftQuest?.id) {
            rows.push({ type: 'inline-draft', quest: inlineDraftQuest, ix: -2 })
        }

        visibleInlineRejectedQuests.forEach((quest, ix) => {
            rows.push({ type: 'inline-rejected', quest, ix: -200 - ix })
        })

        visibleInlineValidPostedQuests.forEach((quest, ix) => {
            rows.push({ type: 'inline-posted', quest, ix: -100 + ix })
        })

        if (pinnedQuestRef?.id) {
            rows.push({
                type: 'pinned',
                quest: pinnedQuestRef,
                ix: -1,
                naturalFeedDescriptor:
                    naturalFlowEnabled && naturalFeedPinnedQuest
                        ? naturalFeedPinnedQuest
                        : undefined,
            })
        }

        streamQuestRows.forEach(({ quest, ix, naturalFeedDescriptor }, questRowIndex) => {
            rows.push({
                type: 'quest',
                quest,
                ix,
                isLastQuest: questRowIndex === streamQuestRows.length - 1,
                naturalFeedDescriptor,
            })
        })

        return rows
    }, [
        inlineDraftQuest,
        naturalFeedPinnedQuest,
        naturalFlowEnabled,
        pinnedQuestRef,
        showInlineNewQuest,
        streamQuestRows,
        visibleInlineRejectedQuests,
        visibleInlineValidPostedQuests,
    ])
    const shouldRenderPaginationTail =
        !naturalFlowEnabled && (!!hasNextPage || !!isFetchingNextPage)
    const naturalStreamRows = useMemo(
        () =>
            naturalFlowEnabled
                ? virtualContentRows
                : virtualContentRows.filter(row => !rowUsesCardGeometry(row)),
        [naturalFlowEnabled, virtualContentRows],
    )
    const virtualStreamRows = useMemo(
        () => (naturalFlowEnabled ? [] : virtualContentRows.filter(rowUsesCardGeometry)),
        [naturalFlowEnabled, virtualContentRows],
    )
    const paginationTailReserveHeight = shouldRenderPaginationTail
        ? paginationTailHeight + (virtualContentRows.length > 0 ? STREAM_QUEST_ROW_GAP_PX : 0)
        : 0

    useLayoutEffect(() => {
        if (isThinQuest) return

        const resolveWidthElement = () => questListElementRef.current || questStreamRootRef.current
        const updateQuestStreamWidth = () => {
            const element = resolveWidthElement()
            if (!element) return

            const nextWidth = Math.round(
                element.getBoundingClientRect?.().width || element.clientWidth || 0,
            )
            if (!nextWidth) return

            setQuestStreamWidth(current => {
                if (naturalFlowEnabled) return current === nextWidth ? current : nextWidth
                return shouldRemeasureRowWidth(current, nextWidth) ? nextWidth : current
            })
        }

        updateQuestStreamWidth()

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', updateQuestStreamWidth)
            return () => window.removeEventListener('resize', updateQuestStreamWidth)
        }

        const observer = new ResizeObserver(updateQuestStreamWidth)
        const element = resolveWidthElement()
        if (element) observer.observe(element)

        return () => observer.disconnect()
    }, [isThinQuest, naturalFlowEnabled])

    const resolveQuestScrollElement = useCallback(
        () => scrollContainerRef.current,
        [scrollContainerRef],
    )
    // Single enumeration of every input that can change a stream row's reserved height.
    // Static geometry consumes it through the virtualizer size channel so reserve changes
    // resize rows in place instead of remounting whole cards.
    const resolveVirtualStreamRowSignature = useCallback(
        (row: QuestStreamVirtualRow | null | undefined) => {
            if (!row?.quest?.id) return null

            const geometryState = questGeometryStateById[String(row.quest.id)]
            const userGeometryRevision = geometryState?.revision || 0
            return `${resolveQuestGeometryRevision(
                row.quest,
            )}:ui:${userGeometryRevision}:width:${questStreamWidth}`
        },
        [questGeometryStateById, questStreamWidth],
    )
    const resolveVirtualStreamRowResizeIntentSignature = useCallback(
        (row: QuestStreamVirtualRow | null | undefined) => {
            if (!row?.quest?.id) return null

            return resolveQuestStreamResizeIntentSignature(
                questGeometryStateById[String(row.quest.id)],
            )
        },
        [questGeometryStateById],
    )
    const resolveVirtualStreamRowKey = useCallback(
        (row: QuestStreamVirtualRow | null | undefined, fallbackIndex?: number) => {
            if (!row?.quest?.id) return `quest-row-${fallbackIndex ?? 'unknown'}`

            return `${row.type}-${streamQuestRowKey(row.quest, fallbackIndex)}`
        },
        [],
    )
    const resolveVirtualQuestRowHeight = useCallback(
        (quest: QuestType | null | undefined, geometryKey?: string | null) => {
            const geometryState = quest?.id ? questGeometryStateById[String(quest.id)] : undefined
            const estimatedHeight = estimateQuestStreamHeight(
                quest,
                isThinQuest,
                questStreamWidth,
                omitRootCrumb ? { ...geometryState, omitRootCrumb } : geometryState,
            )

            return resolveInitialQuestGeometry({
                questId: quest?.id,
                quest,
                estimatedHeight,
                estimatedWidth: questStreamWidth,
                isThin: isThinQuest,
                geometryKey,
                useRememberedHeight: !staticGeometryEnabled,
            }).height
        },
        [
            isThinQuest,
            omitRootCrumb,
            questGeometryStateById,
            questStreamWidth,
            staticGeometryEnabled,
        ],
    )
    const resolveVirtualStreamRowEstimate = useCallback(
        (row: QuestStreamVirtualRow | null | undefined, index?: number) =>
            resolveVirtualQuestRowHeight(row?.quest, resolveVirtualStreamRowKey(row, index)),
        [resolveVirtualQuestRowHeight, resolveVirtualStreamRowKey],
    )
    const naturalFeedSegments = useMemo(
        () =>
            naturalFlowEnabled
                ? buildNaturalFeedSegments(naturalStreamRows, (row, index) =>
                      resolveVirtualStreamRowKey(row, index),
                  )
                : [],
        [naturalFlowEnabled, naturalStreamRows, resolveVirtualStreamRowKey],
    )
    // Keep the exact committed feed in place while React prepares an appended page at
    // interruptible priority. No provisional heights or empty placeholders are introduced.
    const deferredNaturalFeedSegments = useDeferredValue(naturalFeedSegments)
    const deferredNaturalFeedTail =
        deferredNaturalFeedSegments[deferredNaturalFeedSegments.length - 1]
    const naturalFeedTail = naturalFeedSegments[naturalFeedSegments.length - 1]
    const isNaturalFeedAppend =
        naturalFeedSegments.length > deferredNaturalFeedSegments.length ||
        (naturalFeedSegments.length === deferredNaturalFeedSegments.length &&
            (naturalFeedTail?.rows.length || 0) > (deferredNaturalFeedTail?.rows.length || 0))
    const deferredNaturalFeedIsPrefix = deferredNaturalFeedSegments.every(
        (segment, index) => segment.key === naturalFeedSegments[index]?.key,
    )
    const renderedNaturalFeedSegments =
        isNaturalFeedAppend && deferredNaturalFeedIsPrefix
            ? deferredNaturalFeedSegments
            : naturalFeedSegments
    const renderedNaturalFeedTail =
        renderedNaturalFeedSegments[renderedNaturalFeedSegments.length - 1]
    const naturalFeedPageMaterialized =
        renderedNaturalFeedSegments.length === naturalFeedSegments.length &&
        (renderedNaturalFeedTail?.rows.length || 0) === (naturalFeedTail?.rows.length || 0)
    const resolveNaturalFeedSegmentLayoutSignature = useCallback(
        segment =>
            segment.rows
                .map(row => {
                    const geometryState = row?.quest?.id
                        ? questGeometryStateById[String(row.quest.id)]
                        : undefined
                    const contentGeometryRevision =
                        row.naturalFeedDescriptor?.geometryRevision ||
                        resolveQuestGeometryRevision(row.quest)
                    return `${resolveVirtualStreamRowKey(row)}:${contentGeometryRevision}:ui:${
                        geometryState?.revision || 0
                    }:intent:${
                        resolveQuestStreamResizeIntentSignature(geometryState) || 'none'
                    }:width:${questStreamWidth}`
                })
                .join('|'),
        [questGeometryStateById, questStreamWidth, resolveVirtualStreamRowKey],
    )
    const resolveNaturalFeedSegmentEstimatedHeight = useCallback(
        segment =>
            segment.rows.reduce((height, row) => {
                if (!row.naturalFeedDescriptor) {
                    return height + resolveVirtualStreamRowEstimate(row)
                }

                const cachedQuest = getCachedQuest(String(row.quest.id))
                return (
                    height +
                    (cachedQuest
                        ? resolveVirtualQuestRowHeight(cachedQuest, resolveVirtualStreamRowKey(row))
                        : 600)
                )
            }, Math.max(0, segment.rows.length - 1) * STREAM_QUEST_ROW_GAP_PX),
        [resolveVirtualQuestRowHeight, resolveVirtualStreamRowEstimate, resolveVirtualStreamRowKey],
    )
    // Keep geometry derivation outside the virtualizer's scroll-coupled callbacks. TanStack
    // Virtual asks for keys/signatures/estimates while the user scrolls; those paths must read
    // cheap row-model scalars rather than walking quest answers and hashing fingerprints.
    const baseVirtualStreamRowGeometry = useMemo(
        () =>
            virtualStreamRows.map((row, index) => {
                const key = resolveVirtualStreamRowKey(row, index)
                const estimatedHeight = resolveVirtualStreamRowEstimate(row, index)
                const signature = staticGeometryEnabled
                    ? resolveVirtualStreamRowSignature(row)
                    : null
                const editingAnswerIds =
                    staticGeometryEnabled && row?.quest?.id
                        ? questGeometryStateById[String(row.quest.id)]?.editingAnswerIds
                        : undefined
                const staticSignature =
                    signature == null || !row?.quest?.id
                        ? signature
                        : editingAnswerIds?.length
                        ? `${signature}:editing:${editingAnswerIds.join(',')}`
                        : signature
                const resizeIntentSignature = staticGeometryEnabled
                    ? resolveVirtualStreamRowResizeIntentSignature(row)
                    : null

                return {
                    key,
                    estimatedHeight,
                    staticSignature,
                    resizeIntentSignature,
                }
            }),
        [
            questGeometryStateById,
            resolveVirtualStreamRowEstimate,
            resolveVirtualStreamRowKey,
            resolveVirtualStreamRowResizeIntentSignature,
            resolveVirtualStreamRowSignature,
            staticGeometryEnabled,
            virtualStreamRows,
        ],
    )
    const { registerRow: registerStaticQuestRow, rows: virtualStreamRowGeometry } =
        useStaticGeometryRowMeasurements({
            enabled: staticGeometryEnabled && !naturalFlowEnabled,
            rows: baseVirtualStreamRowGeometry,
            resolveScrollElement: resolveQuestScrollElement,
            measureRow: readStaticQuestRowMeasurement,
        })
    const estimatedLoadedStreamHeight = useMemo(() => {
        if (virtualStreamRowGeometry.length === 0) return 0

        const rowsHeight = virtualStreamRowGeometry.reduce((total, row) => {
            return total + row.estimatedHeight
        }, 0)

        return (
            rowsHeight + STREAM_QUEST_ROW_GAP_PX * Math.max(0, virtualStreamRowGeometry.length - 1)
        )
    }, [virtualStreamRowGeometry])

    useLayoutEffect(() => {
        const previousLoadedHeight = previousLoadedStreamHeightRef.current
        previousLoadedStreamHeightRef.current = estimatedLoadedStreamHeight

        if (!shouldRenderPaginationTail) {
            paginationTailCarryRef.current = 0
            setPaginationTailHeight(STREAM_PAGINATION_TAIL_RESERVE_PX)
            return
        }

        if (previousLoadedHeight == null) return

        const loadedGrowth = estimatedLoadedStreamHeight - previousLoadedHeight
        if (loadedGrowth === 0) return

        // Static geometry: symmetric accounting. Growth eats the tail and shrinkage re-inflates
        // it (both clamped), so total scrollHeight stays steady whether estimates move up or
        // down and regardless of scroll direction — asymmetry here is what produced multi-
        // thousand-px scrollbar steps at page boundaries. Whatever the clamp band cannot
        // absorb is carried, so saturated swings cancel across page cycles instead of
        // accumulating as scrollbar steps.
        if (staticGeometryEnabled) {
            setPaginationTailHeight(current => {
                const desired = current - loadedGrowth + paginationTailCarryRef.current
                const next = Math.min(
                    STREAM_PAGINATION_TAIL_RESERVE_PX,
                    Math.max(STREAM_PAGINATION_TAIL_MIN_PX, desired),
                )
                paginationTailCarryRef.current = desired - next
                return next
            })
            return
        }

        if (streamScrollDirectionRef.current === 'up') return
        if (loadedGrowth <= 0) return

        setPaginationTailHeight(current =>
            Math.max(STREAM_PAGINATION_TAIL_MIN_PX, current - loadedGrowth),
        )
    }, [estimatedLoadedStreamHeight, shouldRenderPaginationTail, staticGeometryEnabled])

    const prepareForVirtualPageFetch = useCallback(() => {
        paginationTailCarryRef.current = 0
        setPaginationTailHeight(current => Math.max(current, STREAM_PAGINATION_TAIL_RESERVE_PX))
    }, [])

    useEffect(() => {
        if (
            !naturalFlowEnabled ||
            !naturalFeedPageMaterialized ||
            !hasNextPage ||
            isFetchingNextPage
        ) {
            return
        }

        const sentinel = naturalPaginationSentinelRef.current
        const scrollElement = scrollContainerRef.current
        if (!sentinel || !scrollElement || typeof IntersectionObserver === 'undefined') return

        let idleHandle: number | null = null
        let idleUsesRequestIdleCallback = false
        const cancelIdleFetch = () => {
            if (idleHandle == null) return

            const idleWindow = window as IdleWindow
            if (
                idleUsesRequestIdleCallback &&
                typeof idleWindow.cancelIdleCallback === 'function'
            ) {
                idleWindow.cancelIdleCallback(idleHandle)
            } else {
                window.clearTimeout(idleHandle)
            }
            idleHandle = null
        }
        const fetchAtIdle = () => {
            idleHandle = null
            if (streamPageFetchInFlightRef.current) return

            streamPageFetchInFlightRef.current = true
            Promise.resolve(fetchNextPage()).finally(() => {
                streamPageFetchInFlightRef.current = false
            })
        }
        const scheduleIdleFetch = () => {
            if (idleHandle != null || streamPageFetchInFlightRef.current) return

            const idleWindow = window as IdleWindow
            if (typeof idleWindow.requestIdleCallback === 'function') {
                idleUsesRequestIdleCallback = true
                idleHandle = idleWindow.requestIdleCallback(fetchAtIdle, { timeout: 250 })
            } else {
                idleUsesRequestIdleCallback = false
                idleHandle = window.setTimeout(fetchAtIdle, 100)
            }
        }
        const observer = new IntersectionObserver(
            entries => {
                if (!entries.some(entry => entry.isIntersecting)) return
                scheduleIdleFetch()
            },
            {
                root: scrollElement,
                rootMargin: `${STREAM_PAGE_PREFETCH_DISTANCE_PX}px 0px`,
                threshold: 0,
            },
        )
        observer.observe(sentinel)
        return () => {
            observer.disconnect()
            cancelIdleFetch()
        }
    }, [
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        naturalFeedPageMaterialized,
        naturalFlowEnabled,
        scrollContainerRef,
        streamQuestRows.length,
    ])

    const renderQuestRow = useCallback(
        (quest, ix, isLastQuest, geometryKey, estimatedHeight, naturalFlow = false) => (
            <VirtualQuestInView
                key={quest.id}
                questId={quest.id}
                quest={quest}
                estimatedHeight={estimatedHeight}
                estimatedWidth={questStreamWidth}
                isThin={isThinQuest}
                geometryKey={geometryKey}
                staticGeometry={staticGeometryEnabled}
                naturalFlow={naturalFlow}
                position={ix}
            >
                <div
                    key={`${quest.id}`}
                    className={cn(
                        isThinQuest ? styles.thinQuestContainer : styles.questContainer,
                        isLastQuest && styles.lastQuestContainer,
                        'quest-container',
                    )}
                >
                    {isStarred ? (
                        <ThinQuest
                            quest={quest}
                            contextStyles={styles.thinQuest}
                            showStream={true}
                        />
                    ) : (
                        <Quest
                            panelId={panelId}
                            panel={panel}
                            quest={quest}
                            filter={filter}
                            query={filter?.query}
                            ix={ix}
                            onAnswerTextCollapsedChange={handleAnswerTextCollapsedChange}
                            expandedAnswerIds={
                                questGeometryStateById[String(quest.id)]?.expandedAnswerIds
                            }
                            naturalFeedLayout={naturalFlow}
                        />
                    )}
                </div>
            </VirtualQuestInView>
        ),
        [
            filter,
            handleAnswerTextCollapsedChange,
            isStarred,
            isThinQuest,
            panel,
            panelId,
            questStreamWidth,
            questGeometryStateById,
            staticGeometryEnabled,
        ],
    )

    const renderVirtualStreamRow = useCallback(
        (row: QuestStreamVirtualRow, rowIndex?: number, naturalFlow = false) => {
            const rowGeometry = rowIndex == null ? null : virtualStreamRowGeometry[rowIndex]
            const geometryKey = rowGeometry?.key ?? resolveVirtualStreamRowKey(row, rowIndex)
            const estimatedHeight = rowGeometry?.estimatedHeight ?? 1

            if (row.type === 'inline-draft') {
                return (
                    <VirtualQuestInView
                        key={`inline-draft-${row.quest.id}`}
                        questId={row.quest.id}
                        quest={row.quest}
                        estimatedHeight={estimatedHeight}
                        estimatedWidth={questStreamWidth}
                        isThin={isThinQuest}
                        geometryKey={geometryKey}
                        staticGeometry={staticGeometryEnabled}
                        naturalFlow={naturalFlow}
                    >
                        <div className={cn(styles.questContainer, 'quest-container')}>
                            <Quest
                                key={`inline-draft-quest-${row.quest.id}`}
                                panelId={panelId}
                                panel={panel}
                                quest={row.quest}
                                filter={filter}
                                query={filter?.query}
                                ix={row.ix}
                                onQuestPublished={handleInlineQuestPublished}
                                keepInlineAfterPublish={true}
                                hideQuestHeader={true}
                                inlineCompactComposer={true}
                                onAnswerTextCollapsedChange={handleAnswerTextCollapsedChange}
                                expandedAnswerIds={
                                    questGeometryStateById[String(row.quest.id)]?.expandedAnswerIds
                                }
                                naturalFeedLayout={naturalFlow}
                            />
                        </div>
                    </VirtualQuestInView>
                )
            }

            if (row.type === 'inline-rejected') {
                return (
                    <VirtualQuestInView
                        key={`inline-rejected-${row.quest.id}`}
                        questId={row.quest.id}
                        quest={row.quest}
                        estimatedHeight={estimatedHeight}
                        estimatedWidth={questStreamWidth}
                        isThin={isThinQuest}
                        geometryKey={geometryKey}
                        staticGeometry={staticGeometryEnabled}
                        naturalFlow={naturalFlow}
                    >
                        <div className={cn(styles.questContainer, 'quest-container')}>
                            <QuestNotFound
                                error={{
                                    status: row.quest.localPublishErrorStatus,
                                    message: row.quest.localPublishErrorMessage,
                                }}
                            />
                        </div>
                    </VirtualQuestInView>
                )
            }

            if (row.type === 'inline-posted') {
                return (
                    <VirtualQuestInView
                        key={`inline-posted-${row.quest.id}`}
                        questId={row.quest.id}
                        quest={row.quest}
                        estimatedHeight={estimatedHeight}
                        estimatedWidth={questStreamWidth}
                        isThin={isThinQuest}
                        geometryKey={geometryKey}
                        staticGeometry={staticGeometryEnabled}
                        naturalFlow={naturalFlow}
                        position={row.ix}
                    >
                        <div className={cn(styles.questContainer, 'quest-container')}>
                            <Quest
                                panelId={panelId}
                                panel={panel}
                                quest={row.quest}
                                filter={filter}
                                query={filter?.query}
                                ix={row.ix}
                                onAnswerTextCollapsedChange={handleAnswerTextCollapsedChange}
                                expandedAnswerIds={
                                    questGeometryStateById[String(row.quest.id)]?.expandedAnswerIds
                                }
                                naturalFeedLayout={naturalFlow}
                            />
                        </div>
                    </VirtualQuestInView>
                )
            }

            return renderQuestRow(
                row.quest,
                row.ix,
                row.type === 'quest' && row.isLastQuest,
                geometryKey,
                estimatedHeight,
                naturalFlow,
            )
        },
        [
            filter,
            handleInlineQuestPublished,
            handleAnswerTextCollapsedChange,
            isThinQuest,
            panel,
            panelId,
            questStreamWidth,
            questGeometryStateById,
            renderQuestRow,
            resolveVirtualStreamRowKey,
            staticGeometryEnabled,
            virtualStreamRowGeometry,
        ],
    )

    const renderNaturalFeedRow = useCallback(
        (row: QuestStreamVirtualRow, index: number, taskQueue: NaturalFeedIdleTaskQueue) => {
            const renderHydratedRow = (hydratedQuest: QuestType) => (
                <ScrollResizeCompensationProvider compensate={false}>
                    <RowGeometryEventsProvider
                        questId={hydratedQuest.id}
                        expandedTweetAnswerIds={
                            questGeometryStateById[String(hydratedQuest.id)]?.expandedTweetAnswerIds
                        }
                        onTweetExpandedChange={handleTweetExpandedChange}
                        onAnswerEditingChange={handleAnswerEditingChange}
                    >
                        {renderVirtualStreamRow({ ...row, quest: hydratedQuest }, undefined, true)}
                    </RowGeometryEventsProvider>
                </ScrollResizeCompensationProvider>
            )

            return (
                <div
                    key={resolveVirtualStreamRowKey(row, index)}
                    data-stream-natural-row={row.type}
                    data-stream-row-key={resolveVirtualStreamRowKey(row, index)}
                    className={styles.naturalStreamRow}
                >
                    {row.naturalFeedDescriptor ? (
                        <NaturalFeedHydratedQuestRow
                            descriptor={row.naturalFeedDescriptor}
                            estimatedHeight={resolveNaturalFeedSegmentEstimatedHeight({
                                key: resolveVirtualStreamRowKey(row, index),
                                rows: [row],
                            })}
                            isPanelHidden={hide}
                            isPanelPrewarming={Boolean(panelContentPrewarming)}
                            taskQueue={taskQueue}
                            render={renderHydratedRow}
                        />
                    ) : (
                        renderHydratedRow(row.quest)
                    )}
                </div>
            )
        },
        [
            handleAnswerEditingChange,
            handleTweetExpandedChange,
            hide,
            panelContentPrewarming,
            questGeometryStateById,
            renderVirtualStreamRow,
            resolveNaturalFeedSegmentEstimatedHeight,
            resolveVirtualStreamRowKey,
        ],
    )

    const prefetchNaturalFeedSegment = useCallback(
        (segment: NaturalFeedSegmentModel<QuestStreamVirtualRow>) => {
            queueStreamQuestHydration(segment.rows.map(row => row.naturalFeedDescriptor?.row?.id))
        },
        [],
    )

    return (
        <div ref={questStreamRootRef} className={styles.questStreamComp}>
            {!hideSearchHeader && (
                <div
                    className={cn(
                        isThinQuest
                            ? styles.thinSearchHeaderContainer
                            : styles.searchHeaderContainer,
                        isMobile && !isThinQuest && 'full-bleed-quests',
                    )}
                >
                    {isSearch ? (
                        <Search
                            filter={filter}
                            setIsRefreshing={handleSetIsRefreshing}
                            quest={quests?.[0]}
                        />
                    ) : isUserProfile ? (
                        <UserProfile
                            filter={filter}
                            setIsRefreshing={handleSetIsRefreshing}
                            quest={quests?.[0]}
                            panel={panel}
                        />
                    ) : (
                        <SearchHeader
                            filter={filter}
                            setIsRefreshing={handleSetIsRefreshing}
                            quest={quests?.[0]}
                        />
                    )}
                </div>
            )}

            {isDirectMessages && (
                <DirectMessageStarter
                    quests={streamQuestRows.map(row => row.quest)}
                    panelId={panelId}
                />
            )}

            {isTeamLoading && hasSingleTeamFilter && streamTeamId ? (
                <Loading />
            ) : isPrivateStreamStub ? (
                <PrivateStreamPlaceholder />
            ) : isFetching && !(naturalFlowEnabled ? naturalFeedRows.length : quests.length) ? (
                <Loading />
            ) : isError && !(naturalFlowEnabled ? naturalFeedRows.length : quests.length) ? (
                <div className={styles.emptyParticipants} role="alert">
                    <div className={styles.emptyParticipantsMessage}>
                        Couldn't load. Check your connection.
                    </div>
                    <button
                        type="button"
                        className={styles.streamLoadRetry}
                        onClick={() => void refetch?.()}
                    >
                        Try again
                    </button>
                </div>
            ) : !(naturalFlowEnabled ? naturalFeedRows.length : quests.length) &&
              filter.participants ? (
                <div className={styles.emptyParticipants}>
                    <div className={styles.emptyParticipantsMessage}>No conversations yet</div>
                </div>
            ) : (
                <ErrorBoundary label={`QuestList with filter ${JSON.stringify(filter)}`}>
                    <div
                        ref={questListElementRef}
                        style={
                            {
                                '--stream-quest-row-gap': `${STREAM_QUEST_ROW_GAP_PX}px`,
                            } as React.CSSProperties
                        }
                        className={cn(
                            'quest-list',
                            // Full-bleed cards on phones: the marker consumes the scroll
                            // container's mobile gutter (see panel.module.scss) and squares
                            // off card sides (quest.module.scss). Thin rows keep gutters.
                            isMobile && !isThinQuest && 'full-bleed-quests',
                        )}
                    >
                        {naturalFlowEnabled ? (
                            <NaturalFeedSegments
                                backwardPrefetchRootMargin={
                                    NATURAL_FEED_BACKWARD_PREFETCH_ROOT_MARGIN
                                }
                                forwardPrefetchRootMargin={
                                    NATURAL_FEED_FORWARD_PREFETCH_ROOT_MARGIN
                                }
                                segments={renderedNaturalFeedSegments}
                                sessionKey={naturalFeedSessionKey || inlineDraftFilterKey}
                                getEstimatedHeight={resolveNaturalFeedSegmentEstimatedHeight}
                                getLayoutSignature={resolveNaturalFeedSegmentLayoutSignature}
                                isAlwaysMounted={segment =>
                                    segment.rows.some(row => row.type !== 'quest')
                                }
                                onPrefetchSegment={prefetchNaturalFeedSegment}
                                prefetchRootMargin={NATURAL_FEED_PREFETCH_ROOT_MARGIN}
                                renderRow={renderNaturalFeedRow}
                                suspended={hide}
                            />
                        ) : (
                            naturalStreamRows.map((row, index) => {
                                const geometryState = row.quest?.id
                                    ? questGeometryStateById[String(row.quest.id)]
                                    : undefined
                                const hasFollowingRow =
                                    index < naturalStreamRows.length - 1 ||
                                    virtualStreamRows.length > 0

                                return (
                                    <div
                                        key={resolveVirtualStreamRowKey(row, index)}
                                        data-stream-natural-row={row.type}
                                        data-stream-row-key={resolveVirtualStreamRowKey(row, index)}
                                        className={styles.naturalStreamRow}
                                        style={{
                                            marginBottom: hasFollowingRow
                                                ? STREAM_QUEST_ROW_GAP_PX
                                                : undefined,
                                        }}
                                    >
                                        <ScrollResizeCompensationProvider compensate={false}>
                                            <RowGeometryEventsProvider
                                                questId={row.quest?.id}
                                                expandedTweetAnswerIds={
                                                    geometryState?.expandedTweetAnswerIds
                                                }
                                                onTweetExpandedChange={handleTweetExpandedChange}
                                                onAnswerEditingChange={handleAnswerEditingChange}
                                            >
                                                {renderVirtualStreamRow(row)}
                                            </RowGeometryEventsProvider>
                                        </ScrollResizeCompensationProvider>
                                    </div>
                                )
                            })
                        )}
                        {!naturalFlowEnabled && (
                            <VirtualQuestViewport
                                rows={virtualStreamRows}
                                geometryRows={virtualStreamRowGeometry}
                                getQuestId={row =>
                                    row.quest?.id == null ? null : String(row.quest.id)
                                }
                                geometryStateByQuestId={questGeometryStateById}
                                renderRow={(row, index) => renderVirtualStreamRow(row, index)}
                                resolveScrollElement={resolveQuestScrollElement}
                                staticGeometry={staticGeometryEnabled}
                                measureDeps={[inlineDraftFilterKey, isThinQuest, questStreamWidth]}
                                scrollMarginDeps={[
                                    filter,
                                    inlineDraftFilterKey,
                                    questStreamWidth,
                                    pinnedQuestId,
                                    streamQuestRows.length,
                                    virtualStreamRows.length,
                                ]}
                                observeElementRefs={[questStreamRootRef, questListElementRef]}
                                registerStaticRow={registerStaticQuestRow}
                                onTweetExpandedChange={handleTweetExpandedChange}
                                onAnswerEditingChange={handleAnswerEditingChange}
                                hasNextPage={!!hasNextPage}
                                isFetchingNextPage={!!isFetchingNextPage}
                                fetchNextPage={fetchNextPage}
                                prepareForNextPage={prepareForVirtualPageFetch}
                                scrollDirectionRef={streamScrollDirectionRef}
                                paginationTailHeight={paginationTailHeight}
                                paginationTailReserveHeight={paginationTailReserveHeight}
                                shouldRenderPaginationTail={shouldRenderPaginationTail}
                                overscan={STREAM_VIRTUAL_OVERSCAN}
                                gap={STREAM_QUEST_ROW_GAP_PX}
                                prefetchDistance={STREAM_PAGE_PREFETCH_DISTANCE_PX}
                            />
                        )}
                        {naturalFlowEnabled && naturalFeedPageMaterialized && hasNextPage && (
                            <div
                                ref={naturalPaginationSentinelRef}
                                className={styles.naturalFeedPaginationSentinel}
                                aria-hidden="true"
                            />
                        )}
                    </div>
                </ErrorBoundary>
            )}

            {seoFeedPath && hasNextPage && nextPage != null && (
                <FeedNextPageLink
                    href={feedPageHref(seoFeedPath, nextPage)}
                    onLoadMore={fetchNextPage}
                    isLoading={!!isFetchingNextPage}
                />
            )}
            {isFetchingNextPage && (
                <div
                    className={cn(
                        styles.loadingMore,
                        naturalFlowEnabled && styles.naturalFeedLoadingMore,
                    )}
                >
                    <i className="fa fa-circle-o-notch fa-spin fa-lg" />
                </div>
            )}
        </div>
    )
}

function QuestStreamOrdinals({ filter, hideSearchHeader = false }: QuestStreamProps) {
    const { panel, setIsRefreshing } = usePanelContext()

    const isUserProfile = filter.user || filter?.user_ordinals
    const isSearch = filter.search
    const isThinQuest = false

    const handleSetIsRefreshing = value => {
        if (setIsRefreshing) setIsRefreshing(value)
    }

    return (
        <div className={styles.questStreamComp}>
            {!hideSearchHeader && (
                <div
                    className={cn(
                        styles.searchHeaderContainer,
                        isThinQuest && styles.thinSearchHeaderContainer,
                        isMobile && !isThinQuest && 'full-bleed-quests',
                    )}
                >
                    {isSearch ? (
                        <Search
                            filter={filter}
                            setIsRefreshing={handleSetIsRefreshing}
                            quest={null}
                        />
                    ) : isUserProfile ? (
                        <UserProfile
                            filter={filter}
                            setIsRefreshing={handleSetIsRefreshing}
                            quest={null}
                            panel={panel}
                        />
                    ) : (
                        <SearchHeader
                            filter={filter}
                            setIsRefreshing={handleSetIsRefreshing}
                            quest={null}
                        />
                    )}
                </div>
            )}

            <OrdinalsMediaStream filter={filter} />
        </div>
    )
}

export default function QuestStream({ filter, hideSearchHeader = false }: QuestStreamProps) {
    const isOrdinalsMediaStream = !filter?.search && (filter?.ordinals || filter?.user_ordinals)
    return isOrdinalsMediaStream ? (
        <QuestStreamOrdinals filter={filter} hideSearchHeader={hideSearchHeader} />
    ) : (
        <QuestStreamQuests filter={filter} hideSearchHeader={hideSearchHeader} />
    )
}
